import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faMapMarkerAlt,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faXTwitter,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import { faInstagram, faTiktok } from "@fortawesome/free-brands-svg-icons";
import "./Footer.css";

const Footer = () => {
  const [expanded, setExpanded] = useState(false);
  const [footerInView, setFooterInView] = useState(false);
  const footerRef = useRef(null);
  const canvasRef = useRef(null);
  const socialPillRef = useRef(null);
  const locationWipeRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0, tx: 0, ty: 0, active: false });

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  // Efecto sincronizado cada 15 s: Wipe en Ubicación y máquina de escribir en Teléfono
  const FULL_PHONE = "246 465 03 40";
  const FULL_EXT = "Ext. 111";
  const FULL_LOC_LINE1 = "Ex-Fábrica San Manuel";
  const FULL_LOC_LINE2 = "S/N, Barrio Nuevo";
  const CHAR_DELAY = 150; // ~150 ms por carácter en Teléfono
  const CYCLE_INTERVAL = 15000; // Ciclo de 15 s exactos

  // Estados Teléfono
  const [typedPhone, setTypedPhone] = useState(FULL_PHONE);
  const [typedExt, setTypedExt] = useState(FULL_EXT);
  const [activeCursor, setActiveCursor] = useState(null); // 'phone' | 'ext' | null

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setTypedPhone(FULL_PHONE);
      setTypedExt(FULL_EXT);
      setActiveCursor(null);
      return;
    }

    if (!footerInView) return;

    let isCancelled = false;
    let cycleIntervalId = null;
    let activeTimeouts = [];

    const clearAllTimeouts = () => {
      activeTimeouts.forEach(clearTimeout);
      activeTimeouts = [];
    };

    const addTimeout = (fn, delay) => {
      const id = setTimeout(() => {
        if (!isCancelled && !document.hidden) {
          fn();
        }
      }, delay);
      activeTimeouts.push(id);
      return id;
    };

    const startSyncCycle = () => {
      if (document.hidden || isCancelled) return;

      clearAllTimeouts();

      // --- 1. Reiniciar animación Wipe en Ubicación ---
      if (locationWipeRef.current && !prefersReducedMotion) {
        locationWipeRef.current.classList.remove("is-wipe-active");
        void locationWipeRef.current.offsetWidth; // Forzar reflujo para reiniciar keyframes CSS
        locationWipeRef.current.classList.add("is-wipe-active");
      }

      // --- 2. Reiniciar escritura de Teléfono (Máquina de escribir) ---
      setTypedPhone("");
      setTypedExt("");
      setActiveCursor("phone");

      let pIdx = 0;
      let eIdx = 0;

      const typePhoneChar = () => {
        if (pIdx < FULL_PHONE.length) {
          pIdx++;
          setTypedPhone(FULL_PHONE.slice(0, pIdx));
          addTimeout(typePhoneChar, CHAR_DELAY);
        } else {
          setActiveCursor("ext");
          addTimeout(typeExtChar, 200);
        }
      };

      const typeExtChar = () => {
        if (eIdx < FULL_EXT.length) {
          eIdx++;
          setTypedExt(FULL_EXT.slice(0, eIdx));
          addTimeout(typeExtChar, CHAR_DELAY);
        } else {
          setActiveCursor(null);
        }
      };

      addTimeout(typePhoneChar, CHAR_DELAY);
    };

    // Primera activación sincronizada al entrar en pantalla el footer
    startSyncCycle();

    // Repetir el ciclo sincronizado cada 15 segundos
    cycleIntervalId = setInterval(() => {
      if (!document.hidden && !isCancelled) {
        startSyncCycle();
      }
    }, CYCLE_INTERVAL);

    const onVisibilityChange = () => {
      if (!document.hidden && !isCancelled) {
        startSyncCycle();
      } else {
        clearAllTimeouts();
        if (locationWipeRef.current) {
          locationWipeRef.current.classList.remove("is-wipe-active");
        }
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      isCancelled = true;
      if (cycleIntervalId) clearInterval(cycleIntervalId);
      clearAllTimeouts();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [footerInView]);

  // Observador de entrada con IntersectionObserver para aparición escalonada
  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setFooterInView(true);
      return;
    }

    const entryObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setFooterInView(true);
          entryObserver.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    entryObserver.observe(footer);

    return () => {
      entryObserver.disconnect();
    };
  }, []);

  // Partículas flotantes con canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    const footer = footerRef.current;
    if (!canvas || !footer) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId = null;
    let isVisible = true;
    let isTabActive = true;
    const isTouch = window.matchMedia("(hover: none)").matches;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let width = 0;
    let height = 0;

    const resize = () => {
      const rect = footer.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const resizeObserver = new ResizeObserver(() => {
      resize();
    });
    resizeObserver.observe(footer);
    window.addEventListener("resize", resize);
    resize();

    // Mouse tracking con ref para física de partículas
    const mouse = mouseRef.current;
    mouse.x = width / 2;
    mouse.y = height / 2;
    mouse.tx = width / 2;
    mouse.ty = height / 2;
    mouse.active = false;

    const onMouseMove = (e) => {
      if (isTouch || prefersReducedMotion) return;
      const rect = footer.getBoundingClientRect();
      mouse.tx = e.clientX - rect.left;
      mouse.ty = e.clientY - rect.top;
      mouse.active = true;
    };

    const onMouseLeave = () => {
      mouse.active = false;
    };

    if (!isTouch && !prefersReducedMotion) {
      footer.addEventListener("mousemove", onMouseMove);
      footer.addEventListener("mouseleave", onMouseLeave);
    }

    // ~35 partículas blancas pequeñas que suben lentamente con opacidad variable
    const particleCount = prefersReducedMotion ? 0 : (isTouch ? 18 : 35);
    const particles = Array.from({ length: particleCount }, () => ({
      x: Math.random() * (width || 800),
      y: Math.random() * (height || 220),
      radius: 0.8 + Math.random() * 1.5, // 0.8px a 2.3px
      speedY: 0.18 + Math.random() * 0.32, // Suben lentamente
      speedX: (Math.random() - 0.5) * 0.15, // Leve balanceo
      alpha: 0.15 + Math.random() * 0.55, // Opacidad variable
      fadeSpeed: 0.003 + Math.random() * 0.006,
      increasing: Math.random() > 0.5,
      glow: 0,
    }));

    const render = () => {
      if (!ctx || width === 0 || height === 0) return;

      ctx.clearRect(0, 0, width, height);

      // Suavizado del mouse con lerp
      if (mouse.active) {
        mouse.x += (mouse.tx - mouse.x) * 0.08;
        mouse.y += (mouse.ty - mouse.y) * 0.08;
      }

      const R = 95;

      particles.forEach((p) => {
        // Desvío suave ante cursor
        if (mouse.active && !isTouch && !prefersReducedMotion) {
          const dx = p.x - mouse.x;
          const dy = p.y - mouse.y;
          const d = Math.hypot(dx, dy) || 1;
          if (d < R) {
            const f = 1 - d / R;
            p.x += (dx / d) * f * 1.1;
            p.y += (dy / d) * f * 1.1;
            p.glow = Math.min(1, p.glow + 0.08);
          } else {
            p.glow *= 0.95;
          }
        } else {
          p.glow *= 0.95;
        }

        // Variación suave de opacidad
        if (p.increasing) {
          p.alpha += p.fadeSpeed;
          if (p.alpha >= 0.75) p.increasing = false;
        } else {
          p.alpha -= p.fadeSpeed;
          if (p.alpha <= 0.12) p.increasing = true;
        }

        // Movimiento ascendente lento y balanceo horizontal
        p.y -= p.speedY;
        p.x += p.speedX;

        // Reaparecen abajo al salir por arriba
        if (p.y < -8) {
          p.y = height + 4 + Math.random() * 12;
          p.x = Math.random() * width;
          p.glow = 0;
        }
        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;

        const finalAlpha = Math.min(1, p.alpha + p.glow * 0.25);

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${finalAlpha})`;
        ctx.shadowColor = "rgba(255, 255, 255, 0.75)";
        ctx.shadowBlur = 3 + p.glow * 5;
        ctx.fill();
      });
    };

    if (prefersReducedMotion) {
      if (ctx && width && height) ctx.clearRect(0, 0, width, height);
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("resize", resize);
      };
    }

    let lastFrameTime = 0;
    const fpsLimit = width <= 600 ? 30 : 60;
    const frameInterval = 1000 / fpsLimit;

    const loop = (currentTime) => {
      animId = requestAnimationFrame(loop);
      if (!isVisible || !isTabActive) return;

      const elapsed = currentTime - lastFrameTime;
      if (elapsed > frameInterval) {
        lastFrameTime = currentTime - (elapsed % frameInterval);
        render();
      }
    };

    animId = requestAnimationFrame(loop);

    const observer = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
    });
    observer.observe(footer);

    const onVisibilityChange = () => {
      isTabActive = !document.hidden;
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (animId) cancelAnimationFrame(animId);
      resizeObserver.disconnect();
      observer.disconnect();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (!isTouch && !prefersReducedMotion) {
        footer.removeEventListener("mousemove", onMouseMove);
        footer.removeEventListener("mouseleave", onMouseLeave);
      }
    };
  }, []);

  // Efecto cada 10 segundos en la cápsula de redes sociales
  useEffect(() => {
    const pill = socialPillRef.current;
    if (!pill) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) return;

    let isVisible = true;
    let isTabActive = !document.hidden;
    let isAnimating = false;
    let intervalId = null;
    let activeAnimations = [];

    const playRevealAnimation = () => {
      if (isAnimating || !isVisible || !isTabActive) return;

      const wrappers = pill.querySelectorAll(".social-icon-wrapper");
      if (!wrappers || wrappers.length < 2) return;

      // Cancelar cualquier animación previa pendiente para medir en reposo absoluto
      activeAnimations.forEach((anim) => {
        try {
          anim.cancel();
        } catch (e) {}
      });
      activeAnimations = [];

      // Limpiar estilos inline para garantizar que el cálculo de coordenadas sea exacto
      wrappers.forEach((w) => {
        w.style.transform = "";
        w.style.opacity = "";
      });

      const rect0 = wrappers[0].getBoundingClientRect();
      if (!rect0 || rect0.width === 0) return;

      isAnimating = true;
      let completedCount = 0;
      const totalToAnimate = wrappers.length - 1;

      for (let i = 1; i < wrappers.length; i++) {
        const wrapper = wrappers[i];
        const rectI = wrapper.getBoundingClientRect();
        const deltaX = rect0.left - rectI.left;

        const keyframes = [
          {
            transform: `translateX(${deltaX}px) scale(0.85)`,
            opacity: 0,
          },
          {
            opacity: 0.4,
            offset: 0.15,
          },
          {
            transform: "translateX(0px) scale(1)",
            opacity: 1,
          },
        ];

        const animation = wrapper.animate(keyframes, {
          duration: 1100,
          delay: (i - 1) * 140,
          easing: "cubic-bezier(0.22, 1, 0.36, 1)",
          fill: "none",
        });

        activeAnimations.push(animation);

        const onDone = () => {
          wrapper.style.transform = "";
          wrapper.style.opacity = "";
          completedCount++;
          if (completedCount >= totalToAnimate) {
            isAnimating = false;
          }
        };

        animation.onfinish = onDone;
        animation.oncancel = onDone;
      }
    };

    // Repetir cada 10 segundos
    intervalId = setInterval(() => {
      playRevealAnimation();
    }, 10000);

    const observer = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
    });
    observer.observe(pill);

    const onVisibilityChange = () => {
      isTabActive = !document.hidden;
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      activeAnimations.forEach((anim) => anim.cancel());
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  return (
    <div className="footer-container">
      <div className="footer-tab-container">
        <button
          className="footer-tab"
          onClick={toggleExpand}
          aria-expanded={expanded}
        >
          Más información
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className="tab-icon"
          />
        </button>
      </div>

      <footer
        ref={footerRef}
        className={`footer-content ${footerInView ? "is-visible" : ""}`}
      >
        <canvas
          ref={canvasRef}
          className="footer-waves"
          aria-hidden="true"
        />
        {expanded && (
          <div className="additional-info">
            <div className="four-columns-grid">
              <div className="footer-column">
                <div className="tit-foot">Acerca de</div>
                <ul>
                  <li>
                    <a href="./FinesInstitucionales">Fines institucionales</a>
                  </li>
                  <li>
                    <a href="./Historia">Historia</a>
                  </li>
                  <li>
                    <a href="./PrincipiosRectores">Principios rectores</a>
                  </li>
                  <li>
                    <a href="./EstructuraOrganica">Estructura Orgánica</a>
                  </li>
                </ul>
              </div>

              <div className="footer-column">
                <div className="tit-foot">Consejo general</div>
                <ul>
                  <li>
                    <a href="./Integracion">Integración</a>
                  </li>
                  <li>
                    <a href="./Comisiones">
                      Comisiones Permanentes y Temporales
                    </a>
                  </li>
                  <li>
                    <a href="./JGE">Junta General Ejecutiva</a>
                  </li>
                </ul>
              </div>

              <div className="footer-column">
                <div className="tit-foot">Demarcaciones</div>
                <ul>
                  <li>
                    <a href="./Acuerdos">Acuerdos</a>
                  </li>
                  <li>
                    <a href="./Mapas">Mapas</a>
                  </li>
                  <li>
                    <a href="./AcuerdosPJ">Acuerdos Poder Judicial</a>
                  </li>
                  <li>
                    <a href="./MapasPJ">Mapas Poder Judicial</a>
                  </li>
                </ul>
              </div>

              <div className="footer-column">
                <div className="tit-foot">Queremos escucharte</div>
                <ul>
                  <li>
                    <a href="https://encuesta-satisfaccion.itetlax.org.mx/">
                      Encuesta de satisfacción
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Fila principal del footer */}
        <div className="footer-row-container">
          {/* Ubicación (enlace a Google Maps con efecto de revelación suave wipe) */}
          <a
            href="https://maps.app.goo.gl/NtybEoWZGmk63Nrg8"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Ver ubicación en Google Maps: ${FULL_LOC_LINE1}, ${FULL_LOC_LINE2} (se abre en una pestaña nueva)`}
            className="footer-section footer-section-location footer-location-link footer-entry-block footer-delay-1"
          >
            <div className="footer-bubble-icon" aria-hidden="true">
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                className="footer-icon"
              />
            </div>
            <div className="footer-text-group">
              <span className="footer-label">Ubicación</span>
              <div
                ref={locationWipeRef}
                className="footer-value footer-location-address footer-location-wipe-container"
              >
                <span className="footer-location-line footer-location-line-1">
                  <span className="footer-location-text">{FULL_LOC_LINE1}</span>
                </span>
                <br />
                <span className="footer-location-line footer-location-line-2">
                  <span className="footer-location-text">{FULL_LOC_LINE2}</span>
                </span>
              </div>
            </div>
          </a>

          <div className="footer-divider footer-divider-1" aria-hidden="true" />

          {/* Teléfono con efecto máquina de escribir */}
          <div className="footer-section footer-section-phone footer-entry-block footer-delay-2">
            <div className="footer-bubble-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faPhone} className="footer-icon" />
            </div>
            <div className="footer-text-group">
              <span className="footer-label">Teléfono</span>
              <div
                className="footer-value footer-phone-typewriter"
                data-text={`${FULL_PHONE} ${FULL_EXT}`}
                aria-label={`Teléfono: ${FULL_PHONE}, ${FULL_EXT}`}
              >
                {/* Capa fantasma para reservar ancho y alto sin saltos */}
                <div className="typewriter-ghost" aria-hidden="true">
                  <span className="footer-phone-link">{FULL_PHONE}</span>
                  <br />
                  <span>{FULL_EXT}</span>
                </div>

                {/* Capa visible con efecto de máquina de escribir */}
                <div className="typewriter-visible" aria-hidden="true">
                  <a
                    href="tel:2464650340"
                    className="footer-phone-link"
                    tabIndex={0}
                  >
                    {typedPhone}
                    {activeCursor === "phone" && (
                      <span className="typewriter-cursor" aria-hidden="true" />
                    )}
                  </a>
                  <br />
                  <span>
                    {typedExt}
                    {activeCursor === "ext" && (
                      <span className="typewriter-cursor" aria-hidden="true" />
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            className="footer-divider footer-divider-2"
            aria-hidden="true"
          />

          {/* Sección de redes sociales con título superior */}
          <div className="footer-social-section footer-entry-block footer-delay-3">
            <span className="footer-label footer-social-label">
              Conéctate con nosotros
            </span>
            <div
              ref={socialPillRef}
              className="footer-social-pill"
            >
            <span className="social-icon-wrapper" style={{ zIndex: 5 }}>
              <a
                href="https://es-la.facebook.com/InstitutoTlaxcaltecadeElecciones/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon facebook"
                aria-label="Facebook del Instituto Tlaxcalteca de Elecciones"
              >
                <FontAwesomeIcon icon={faFacebookF} className="icon-social" />
              </a>
            </span>

            <span className="social-icon-wrapper" style={{ zIndex: 4 }}>
              <a
                href="https://x.com/ITETLAX"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon x-twitter"
                aria-label="Cuenta oficial de X del ITE"
              >
                <FontAwesomeIcon icon={faXTwitter} className="icon-social" />
              </a>
            </span>

            <span className="social-icon-wrapper" style={{ zIndex: 3 }}>
              <a
                href="https://www.instagram.com/ite_tlaxcala/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon instagram"
                aria-label="Instagram del ITE"
              >
                <FontAwesomeIcon icon={faInstagram} className="icon-social" />
              </a>
            </span>

            <span className="social-icon-wrapper" style={{ zIndex: 2 }}>
              <a
                href="https://www.tiktok.com/@ite.oficial"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon tiktok"
                aria-label="TikTok oficial del ITE"
              >
                <FontAwesomeIcon icon={faTiktok} className="icon-social" />
              </a>
            </span>

            <span className="social-icon-wrapper" style={{ zIndex: 1 }}>
              <a
                href="https://www.youtube.com/channel/UCUBfUX_c54NfhhvZRzK0k1w?themeRefresh=1"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon youtube"
                aria-label="Canal oficial de YouTube del ITE"
              >
                <FontAwesomeIcon icon={faYoutube} className="icon-social" />
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
    </div>
  );
};

export default Footer;
